import React, { useState } from 'react';
import { array, bool, func, string, object } from 'prop-types';

import { connect } from 'react-redux';

import { useFormik } from 'formik';
import * as Yup from 'yup';
import { isEmpty, has } from 'lodash';

import FormControl from '@material-ui/core/FormControl';
import AutoComplete from '../../components/FormFields/Autocomplete';

import EsmsDialog from '../../components/EsmsDialog';

import * as ArchitectActions from '../../actions/architectActions';
import * as ApplicationMessageActions from '../../actions/applicationMessageActions';
import * as CoreActions from '../../actions/coreActions';
import * as SiteActions from '../../actions/siteActions';
import * as UiActions from '../../actions/uiActions';

import classes from './CreateEntityModal.module.css';

const CreateLinkModal = ({
    checkNoExistingAgent,
    checkExistingLinks,
    close,
    clearErrors,
    clearErpEntities,
    erpAgents,
    erpClients,
    erpSites,
    erpTechnicians,
    erpEquipment,
    errors,
    isLoading,
    linkType,
    open,
    selectedNode,
    selectedTechnician,
    setProgressBarLoading,
    techniciansList,
    title,
    toggleLoading,
    ...props
}) => {

    const [ existingSite, setExistingSite ] = useState('');
    const [ noExistingAgent, setNoExistingAgent ] = useState(true);

    let initialValues;
    let helperText;
    let inputTypes = [];
    let ValidationSchema;
    const stateForInputType = {
        agent:      erpAgents,
        client:     erpClients,
        site:       erpSites,
        equipment:  erpEquipment,
        technician: erpTechnicians
    };

    if (linkType === 'client') {
        initialValues = {
            agent:  '',
            client: ''
        };
        ValidationSchema = Yup.object().shape({
            agent:  Yup.string().required(),
            client: Yup.string().required()
        });
        helperText = (
            <div className={classes.labelColumn}>
                <div className={classes.labelHeader}>
                    <img src="/assets/images/chain-flat.svg" alt="new area icon" />
                    <span>Select ERP Client</span>
                </div>
                <div className={classes.labelContent}>
                    <p>
                        A Client is representative of your Customer.
                    </p>
                    <p>
                        Select the ERP Agent where your Customer is located.
                    </p>
                    <p>
                        Select the entity in your ERP system that you'd like to link to this Client.
                    </p>
                </div>
            </div>
        );
        inputTypes = ['agent', 'client'];
    } else if (linkType === 'site') {
        initialValues = {
            agent: '',
            site:  ''
        };
        ValidationSchema = Yup.object().shape({
            agent: Yup.string()
                .test(
                    'agent',
                    'Agent already involved in a link for this BOB Site',
                    function() {
                        return noExistingAgent;
                    }
                )
                .required(),
            site: Yup.string()
                .test(
                    'site',
                    `Site already linked to ${existingSite}.`,
                    function() {
                        return isEmpty(existingSite);
                    }
                ).required().nullable()
        });
        helperText = (
            <div className={classes.labelColumn}>
                <div className={classes.labelHeader}>
                    <img src="/assets/images/chain-flat.svg" alt="link site icon" />
                    <span>Select ERP Site</span>
                </div>
                <div className={classes.labelContent}>
                    <p>
                        A Site is representative of a physical building.
                    </p>
                    <p>
                        Select the ERP Agent where the building you'd like to link is located.
                    </p>
                    <p>
                        Select the entity in your ERP system that you'd like to link to this Site.
                    </p>
                </div>
            </div>
        );
        inputTypes = ['agent', 'client', 'site'];
    } else if (linkType === 'equipmentItem') {
        initialValues = {
            agent:     '',
            equipment: ''
        };
        ValidationSchema = Yup.object().shape({
            agent:     Yup.string().required(),
            equipment: Yup.string().required()
        });
        helperText = (
            <div className={classes.labelColumn}>
                <div className={classes.labelHeader}>
                    <img src="/assets/images/areaTreeIcon.svg" alt="new area icon" />
                    <span>Select ERP Equipment</span>
                </div>
                <div className={classes.labelContent}>
                    <p>
                        An Equipment is representative of an asset within a Site.
                    </p>
                    <p>
                        Select the ERP Agent where the equipment you'd like to link is located.
                    </p>
                    <p>
                        Select the entity in your ERP system that you'd like to link to this Equipment.
                    </p>
                </div>
            </div>
        );
        inputTypes = ['agent', 'client', 'site', 'equipment'];
    } else if (linkType === 'technician') {
        initialValues = {
            agent:      '',
            technician: ''
        };
        ValidationSchema = Yup.object().shape({
            agent:      Yup.string().required(),
            technician: Yup.string().required()
        });
        helperText = (
            <div className={classes.labelColumn}>
                <div className={classes.labelHeader}>
                    <img src="/assets/images/areaTreeIcon.svg" alt="new area icon" />
                    <span>Select ERP Technician</span>
                </div>
                <div className={classes.labelContent}>
                    <p>
                        Select the ERP Agent where the ERP Technician is located.
                    </p>
                    <p>
                        Select an ERP Technician to link to this Technician.
                    </p>
                </div>
            </div>
        );
        inputTypes = ['agent', 'technician'];
    } else {
        initialValues = {};
        helperText = (<div />);
        inputTypes = [];
    }

    const formik = useFormik({
        initialValues,
        validationSchema:   ValidationSchema,
        enableReinitialize: true,
        onSubmit:           async (values, { setSubmitting }) => {
            const {
                createLink
            } = props;

            let isCreated;
            let body;
            switch (linkType) {
                case 'client':
                    body = {
                        erpEntityKind:        'client',
                        externalId:           values.client.id,
                        entityId:             selectedNode.id,
                        agentConfigurationId: values.agent.id
                    };
                    break;
                case 'site':
                    body = {
                        erpEntityKind:        'site',
                        externalId:           values.site.id,
                        entityId:             selectedNode.id,
                        agentConfigurationId: values.agent.id
                    };
                    break;
                case 'equipmentItem':
                    body = {
                        erpEntityKind:        'equipment',
                        externalId:           values.equipment.id,
                        entityId:             selectedNode.id,
                        agentConfigurationId: values.agent.id
                    };
                    break;
                case 'technician':
                    body = {
                        erpEntityKind:        'technician',
                        externalId:           values.technician.id,
                        entityId:             selectedTechnician.id,
                        agentConfigurationId: values.agent.id
                    };
                    break;
                default:
                    body = {};
            }

            clearErrors();
            setSubmitting(true);

            try {
                if (!isLoading) {
                    toggleLoading(true);
                    isCreated = await createLink(body);
                }
                if (isCreated) {
                    setSubmitting(false);
                    toggleLoading(false);

                    handleClose();
                } else {
                    setSubmitting(false);
                    toggleLoading(false);
                }
            } catch (error) {
                setSubmitting(false);
            }
        }
    });

    const handleClose = () => {
        toggleLoading(false);
        clearErpEntities();
        clearErrors();

        close();
    };

    const handleChange = async event => {
        const { value } = event.target;

        props.fetchErpDrilldown(value.type, value.id);
        if (value.type ==='agents') {
            formik.setFieldValue('client', null);
            formik.setFieldValue('site', null);
            formik.setFieldValue('equipment', null);
        } else if (value.type === 'clients') {
            formik.setFieldValue('site', null);
            formik.setFieldValue('equipment', null);
        } else if (value.type === 'sites') {
            const agentId = formik.values.agent.id;
            setExistingSite('');
            setNoExistingAgent(true);

            setProgressBarLoading(true);
            const linkName = await checkExistingLinks(value.id);
            const existingAgentTest = checkNoExistingAgent(agentId);
            setProgressBarLoading(false);

            setNoExistingAgent(existingAgentTest);
            setExistingSite(linkName);

            formik.setFieldValue('equipment', null);
        }

        formik.handleChange(event);
    };

    const onConfirm = () => {
        formik.handleSubmit();
    };

    const isAutoFillArrayEmpty = array => {
        if (!array || array.length < 1) {
            return true;
        }

        if (has(array[0], 'items')) {
            return isEmpty(array[0].items);
        }

        return true;
    };

    return (
        <EsmsDialog
            errors={errors}
            open={open}
            onClose={handleClose}
            title={title}
            onRendered={formik.handleReset}
            onConfirm={onConfirm}
            disabled={formik.isSubmitting}
            isLoading={isLoading}
            confirmText="Create"
        >
            <div className={classes.formColumn}>
                {helperText}
                <div className={classes.inputColumn}>
                    <div className={classes.inputs}>
                        {inputTypes.length && inputTypes.map(inputType =>
                            <FormControl
                                className={classes.inputWrapper}
                                key={inputType}
                            >
                                <AutoComplete
                                    grouped
                                    label={inputType.charAt(0).toUpperCase() + inputType.slice(1)}
                                    name={inputType}
                                    classes={{
                                        inputRoot: classes.inputRoot
                                    }}
                                    className={classes.autoComplete}
                                    onChange={handleChange}
                                    onBlur={formik.handleBlur}
                                    options={stateForInputType[inputType]}
                                    disabled={isAutoFillArrayEmpty(stateForInputType[inputType])}
                                    value={formik.values[inputType]}
                                    error={Boolean(formik.errors[inputType]) && Boolean(formik.touched[inputType])}
                                    helperText={Boolean(formik.touched[inputType]) && formik.errors[inputType]}
                                />
                            </FormControl>
                        )}
                    </div>
                </div>
            </div>
        </EsmsDialog>
    );
};

CreateLinkModal.defaultProps = {
    erpAgents:      [],
    erpClients:     [],
    erpSites:       [],
    erpEquipment:   [],
    erpTechnicians: []
};

CreateLinkModal.propTypes = {
    erpClients:         array,
    erpAgents:          array,
    erpSites:           array,
    erpTechnicians:     array,
    erpEquipment:       array,
    close:              func.isRequired,
    open:               bool.isRequired,
    title:              string.isRequired,
    linkType:           string,
    selectedNode:       object,
    selectedTechnician: object,
    clearErpEntities:   func
};

const mapStateToProps = state => {
    return {
        errors:             state.messages.errors,
        isLoading:          state.ui.isLoading,
        erpAgents:          state.architect.erpAgents,
        erpClients:         state.architect.erpClients,
        erpSites:           state.architect.erpSites,
        erpTechnicians:     state.architect.erpTechnicians,
        erpEquipment:       state.architect.erpEquipment,
        selectedNode:       state.architect.selectedNode,
        selectedTechnician: state.architect.selectedTechnician
    };
};

const mapDispatchToProps = {
    ...ArchitectActions,
    ...ApplicationMessageActions,
    ...CoreActions,
    ...SiteActions,
    ...UiActions
};

export default connect(mapStateToProps, mapDispatchToProps)(CreateLinkModal);
